import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, Clock, FileText, Loader2, Search, X } from 'lucide-react';
import { useAppAuth } from '@/hooks/useAppAuth';
import {
  useKnowledgeBaseTree,
  useKnowledgeBaseItem,
  useSaveKnowledgeBaseItem,
  useDeleteKnowledgeBaseItem,
  useApproveKnowledgeBaseItem,
  useRejectKnowledgeBaseItem,
  useMarkPendingKnowledgeBaseItem,
} from '@/hooks/useKnowledgeBase';
import type { KnowledgeBaseItem } from '@/services/knowledgeBase.service';
import { formatShortDate } from '@/utils/formatDate';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ArticleEditor from '@/components/knowledge-base/ArticleEditor';
import StaleUpdateDialog from '@/components/knowledge-base/StaleUpdateDialog';
import { ApiError } from '@/services/api.service';
import { showToast } from '@/services/toast.service';

/**
 * The knowledge base on a phone: a flat, searchable list of articles that
 * drills into one article, instead of the desktop page's tree-beside-editor
 * split.
 *
 * The folder tree is the casualty, and deliberately so — it's a filing
 * structure you navigate with a mouse, and reproducing it in a quarter of the
 * width buys nothing. Folders survive as the grey label above each group, which
 * is the part that tells you where an article lives. Reading, reviewing what
 * Ellie proposed, and editing an article all work; reorganizing the tree
 * (creating, moving, renaming folders) is desktop-only.
 */

type ListEntry = {
  article: KnowledgeBaseItem;
  folderName: string | null;
  /** A pending update Ellie proposed against this article, if any. */
  pendingUpdate: KnowledgeBaseItem | undefined;
};

export default function MobileKnowledgeBase() {
  const { id: urlArticleId } = useParams<{ id: string }>();
  return urlArticleId ? (
    <MobileArticle articleId={urlArticleId} />
  ) : (
    <MobileArticleList />
  );
}

// ─── List ────────────────────────────────────────────────────────────────────

function MobileArticleList() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const { data: treeData, isLoading } = useKnowledgeBaseTree();
  const items = useMemo(() => treeData?.records || [], [treeData]);

  const entries = useMemo<ListEntry[]>(() => {
    const folderNames = new Map(
      items.filter(i => i.item_type === 'folder').map(i => [i.id, i.name])
    );
    // Pending *updates* are clones pointing at an original; they belong to that
    // original's row rather than being rows of their own — same rule the
    // desktop tree uses.
    const pendingUpdates = new Map<string, KnowledgeBaseItem>();
    for (const item of items) {
      if (item.original_item_id && item.status === 'pending') {
        pendingUpdates.set(item.original_item_id, item);
      }
    }
    return items
      .filter(i => i.item_type === 'article' && !i.original_item_id)
      .map(article => ({
        article,
        folderName: article.parent_id ? folderNames.get(article.parent_id) ?? null : null,
        pendingUpdate: pendingUpdates.get(article.id),
      }));
  }, [items]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter(
      ({ article, folderName }) =>
        article.name.toLowerCase().includes(q) ||
        article.tags?.some(t => t.toLowerCase().includes(q)) ||
        folderName?.toLowerCase().includes(q)
    );
  }, [entries, searchQuery]);

  // Anything awaiting review floats to the top — it's the only thing on this
  // page that's actually waiting on the reader.
  const sorted = useMemo(() => {
    const needsReview = (e: ListEntry) => e.article.status === 'pending' || !!e.pendingUpdate;
    return [...filtered].sort((a, b) => {
      if (needsReview(a) !== needsReview(b)) return needsReview(a) ? -1 : 1;
      return (a.folderName || '').localeCompare(b.folderName || '') ||
        a.article.name.localeCompare(b.article.name);
    });
  }, [filtered]);

  return (
    <div className="h-full flex flex-col bg-background">
      <header className="px-4 py-3 page-header-surface flex-shrink-0">
        <h1 className="text-lg font-semibold text-foreground">Knowledge Base</h1>
        <div className="relative mt-2">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search articles"
            // text-base: anything smaller makes iOS Safari zoom the page on focus.
            className="pl-8 text-base"
          />
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : sorted.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center px-6 gap-2">
            <FileText className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium text-foreground">
              {searchQuery.trim() ? 'No articles match that search' : 'No articles yet'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {sorted.map(entry => (
              <li key={entry.article.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/knowledge-base/article/${entry.article.id}`)}
                  className="w-full text-left px-4 py-3.5 active:bg-accent/50 transition-colors"
                >
                  {entry.folderName && (
                    <p className="text-[11px] text-muted-foreground truncate">{entry.folderName}</p>
                  )}
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-medium text-foreground truncate">
                      {entry.article.name}
                    </span>
                    {entry.article.status === 'pending' && (
                      <Badge variant="default" className="h-5 px-1.5 text-[11px] flex-shrink-0">
                        New
                      </Badge>
                    )}
                    {entry.pendingUpdate && (
                      <Badge variant="outline" className="h-5 px-1.5 text-[11px] flex-shrink-0">
                        Update
                      </Badge>
                    )}
                  </div>
                  {!!entry.article.tags?.length && (
                    <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                      {entry.article.tags.join(' · ')}
                    </p>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ─── Detail ──────────────────────────────────────────────────────────────────

function MobileArticle({ articleId }: { articleId: string }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialEditing = searchParams.get('edit') === 'true';
  const { user } = useAppAuth();

  // Same rule as the desktop page: everyone but a lone ReadOnly_Partner can edit.
  const canManageKB = useMemo(() => {
    if (!user) return false;
    const roles = (user.roles || []).map((r: any) =>
      (typeof r === 'string' ? r : r?.key || r?.name || '').toLowerCase()
    );
    return !(roles.length === 1 && roles[0] === 'readonly_partner');
  }, [user]);

  const { data: treeData } = useKnowledgeBaseTree();
  const items = treeData?.records || [];
  const listed = items.find(i => i.id === articleId);
  const { data: article } = useKnowledgeBaseItem(articleId);

  const pendingUpdateId =
    items.find(i => i.original_item_id === articleId && i.status === 'pending')?.id ?? null;
  const { data: pendingUpdateArticle } = useKnowledgeBaseItem(pendingUpdateId);

  const saveMutation = useSaveKnowledgeBaseItem();
  const deleteMutation = useDeleteKnowledgeBaseItem();
  const approveMutation = useApproveKnowledgeBaseItem();
  const rejectMutation = useRejectKnowledgeBaseItem();
  const markPendingMutation = useMarkPendingKnowledgeBaseItem();
  const [staleUpdate, setStaleUpdate] = useState<KnowledgeBaseItem | null>(null);

  if (!article) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const isPendingNew = listed?.status === 'pending';
  const pendingUpdate = pendingUpdateId ? items.find(i => i.id === pendingUpdateId) : undefined;
  const pendingUpdateCount = items.filter(i => i.original_item_id === articleId && i.status === 'pending').length;

  const approve = async (id: string, force = false) => {
    try {
      await approveMutation.mutateAsync({ id, force });
      showToast.success('Approved');
    } catch (err) {
      if (!force && err instanceof ApiError && err.code === 409) {
        setStaleUpdate(items.find(i => i.id === id) ?? null);
      } else {
        showToast.error('Failed to approve');
      }
    }
  };

  const reject = async (id: string) => {
    try {
      await rejectMutation.mutateAsync(id);
      showToast.success('Rejected');
      // Rejecting the article itself removes it; there's nothing left to show.
      if (id === articleId) navigate('/knowledge-base');
    } catch {
      showToast.error('Failed to reject');
    }
  };

  const save = async (updates: Partial<KnowledgeBaseItem>) => {
    try {
      // ArticleEditor hands back the id in `updates` — same contract the
      // desktop page uses.
      await saveMutation.mutateAsync(updates);
      showToast.success('Article saved');
    } catch {
      showToast.error('Failed to save article');
    }
  };

  const markPending = async () => {
    try {
      await markPendingMutation.mutateAsync(article.id);
      showToast.success('Marked as pending');
    } catch {
      showToast.error('Failed to mark as pending');
    }
  };

  const destroy = async () => {
    try {
      await deleteMutation.mutateAsync(article.id);
      showToast.success('Article deleted');
      navigate('/knowledge-base');
    } catch {
      showToast.error('Failed to delete article');
    }
  };

  return (
    <div className="h-full flex flex-col bg-background">
      <header className="px-2 py-2 page-header-surface flex-shrink-0 flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2 text-muted-foreground"
          onClick={() => navigate('/knowledge-base')}
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Articles
        </Button>
        <span className="text-sm font-medium text-foreground truncate min-w-0 flex-1">
          {article.name}
        </span>
      </header>

      {isPendingNew && (
        <ReviewBanner
          tone="amber"
          message={`Proposed by Ellie${
            formatShortDate(article.created_at) ? ` on ${formatShortDate(article.created_at)}` : ''
          } — pending approval`}
          canManage={canManageKB}
          approveLabel="Approve"
          onApprove={() => approve(article.id)}
          onReject={() => reject(article.id)}
          busy={approveMutation.isPending || rejectMutation.isPending}
        />
      )}

      {pendingUpdate && (
        <ReviewBanner
          tone="blue"
          message={`Ellie proposed an update${
            formatShortDate(pendingUpdate.created_at)
              ? ` on ${formatShortDate(pendingUpdate.created_at)}`
              : ''
          }${pendingUpdateCount > 1 ? ` (+${pendingUpdateCount - 1} more pending)` : ''}`}
          canManage={canManageKB}
          approveLabel="Approve update"
          onApprove={() => approve(pendingUpdate.id)}
          onReject={() => reject(pendingUpdate.id)}
          busy={approveMutation.isPending || rejectMutation.isPending}
        />
      )}

      <div className="flex-1 min-h-0">
        <ArticleEditor
          article={article}
          pendingUpdateArticle={pendingUpdateArticle}
          onSave={save}
          saving={saveMutation.isPending}
          readOnly={!canManageKB}
          initialEditing={initialEditing}
          onMarkPending={markPending}
          onDelete={destroy}
        />
      </div>

      <StaleUpdateDialog
        article={article}
        proposal={staleUpdate}
        onConfirm={() => { if (staleUpdate) approve(staleUpdate.id, true); setStaleUpdate(null); }}
        onCancel={() => setStaleUpdate(null)}
      />
    </div>
  );
}

/**
 * The approve/reject bar. Stacks the buttons under the message rather than
 * beside it — on the desktop page they sit inline, which at phone width
 * squeezes the explanation down to a word or two.
 */
function ReviewBanner({
  tone,
  message,
  canManage,
  approveLabel,
  onApprove,
  onReject,
  busy,
}: {
  tone: 'amber' | 'blue';
  message: string;
  canManage: boolean;
  approveLabel: string;
  onApprove: () => void;
  onReject: () => void;
  busy: boolean;
}) {
  const toneClasses =
    tone === 'amber'
      ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200'
      : 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200';
  const iconClass = tone === 'amber' ? 'text-amber-600' : 'text-blue-600';

  return (
    <div className={`px-4 py-2.5 border-b flex-shrink-0 ${toneClasses}`}>
      <div className="flex items-start gap-2">
        <Clock className={`h-4 w-4 shrink-0 mt-0.5 ${iconClass}`} />
        <span className="text-sm flex-1">{message}</span>
      </div>
      {canManage && (
        <div className="flex items-center gap-2 mt-2">
          <Button size="sm" variant="destructive" onClick={onReject} disabled={busy} className="flex-1">
            <X className="h-3.5 w-3.5 mr-1" />
            Reject
          </Button>
          <Button
            size="sm"
            className="flex-1 bg-success text-success-foreground hover:bg-success/90"
            onClick={onApprove}
            disabled={busy}
          >
            <Check className="h-3.5 w-3.5 mr-1" />
            {approveLabel}
          </Button>
        </div>
      )}
    </div>
  );
}
